import { type JSX, useCallback, useRef } from 'react';

import { useTranslation } from '@cx-modules/i18n-provider';
import { OddsButton } from '@cx-modules/ui';

import { useBetslip } from '../../hooks/useBetslip';
import { useBookmakerData } from '../../hooks/useBookmakerData';
import { useSelector } from '../../providers/StoreProvider';
import { oddsShownType } from '../../types';
import { type Bet } from '../../types';
import { categoryIdToCategoryMap, getTranslatedBetTitle } from '../../utils/labels';
import { NewUserTooltip } from '../NewUserTooltip';

import { MarketExpanderBetName, MarketExpanderBetWrapper, OddsButtonWrapper } from './style';

interface MarketExpanderBetProps {
  bet: Bet & {
    bestOdds: number;
    bestOddsFractional: string;
    bestOddsText: string | number;
  };
  isFirst: boolean;
}

export const MarketExpanderBet = ({
  bet: { bestOdds, bestOddsText, id, line, name, odds },
  isFirst,
}: MarketExpanderBetProps): JSX.Element => {
  const oddsShown = useSelector(state => state.oddsShown);
  const { allowOddsClickout, categoryGroupId, disableStreamer, onOddsButtonClick, renderBadge, translateBetNames } =
    useSelector(state => ({
      allowOddsClickout: state.allowOddsClickout,
      categoryGroupId: state.categoryGroupId,
      disableStreamer: state.disableStreamer,
      onOddsButtonClick: state.onOddsButtonClick,
      renderBadge: state.renderOddsButtonBadge,
      translateBetNames: state.translateBetNames,
    }));

  const categoryNs = categoryIdToCategoryMap?.[categoryGroupId];
  const ns = [categoryNs, 'label', 'bettype'];

  const { t } = useTranslation(ns);

  const betNameContainerRef = useRef<HTMLDivElement | null>(null);
  const betNameRef = useRef<HTMLDivElement | null>(null);
  const buttonWrapperRef = useRef<HTMLDivElement>(null);

  const lineName = line?.name || '';

  const bestOdd = odds.find(odd => odd.decimal === bestOdds && odd.status.toLowerCase() === 'active');

  const steamOrDrift = bestOdd?.movement?.toLowerCase() || '';
  const bookieCode = bestOdd?.bookmakerCode ?? '';
  const { bookmakerData: bookmakerConfig } = useBookmakerData(bookieCode);

  const { isBookieBetSelected, toggleBetslipBet } = useBetslip({
    betId: id,
    betOdds: bestOddsText,
    bookieCode,
  });

  const betTitle = `${name} ${lineName}`.trim();
  const translatedBetTitle = getTranslatedBetTitle({ betName: name, lineName, ns, t });

  const onClickHandler = useCallback(() => {
    if (oddsShown === oddsShownType.OC_NON_CLICKABLE) return;
    if (!allowOddsClickout) return;
    if (onOddsButtonClick) {
      return onOddsButtonClick('best_odds', id, bookieCode, name, bestOddsText || 'SP');
    }
    toggleBetslipBet();
  }, [onOddsButtonClick, toggleBetslipBet, id, bookieCode, name, bestOddsText, oddsShown, allowOddsClickout]);

  const tooltipForNewUser = useSelector(state => state.tooltipForNewUser);

  return (
    <MarketExpanderBetWrapper data-testid="market-bet" ref={betNameContainerRef}>
      <MarketExpanderBetName className="body-text-3" data-testid="bet-name" fontSize="14px" ref={betNameRef}>
        {translateBetNames ? translatedBetTitle : betTitle}
      </MarketExpanderBetName>
      <OddsButtonWrapper ref={buttonWrapperRef}>
        <OddsButton
          bookieCode={bookieCode}
          bookmakerConfig={bookmakerConfig}
          data-testid="bet-odds"
          disableAnimation={disableStreamer}
          disabled={!allowOddsClickout}
          isSelected={isBookieBetSelected}
          isUnsupported={!allowOddsClickout}
          onClick={onClickHandler}
          renderBadge={renderBadge}
          steamOrDrift={steamOrDrift}
        >
          {bestOddsText || 'SP'}
        </OddsButton>
      </OddsButtonWrapper>
      {tooltipForNewUser && isFirst && <NewUserTooltip buttonRef={buttonWrapperRef} orientation="left" />}
    </MarketExpanderBetWrapper>
  );
};
